import { useState, type CSSProperties } from "react";
import { Icon } from "./Icons";
import { fmtDate, relative } from "../lib/format";
import type { DrawingReview } from "../lib/types";

/* The coordination table at the heart of a drawing review: every change request
   the client raised, the studio's response, the revision that carries it and a
   photo highlighting the changed area — compiled with the dates. Shared by the
   studio cockpit (with a Respond action) and the client portal (read-only). */

export interface ChangeRow {
  sheetId: string; sheetCode: string; sheetTitle: string; discipline: string;
  remarkId: string; author: string; requested: string; receivedOn: string; onRevision: string;
  requestPhoto?: string;
  responded: boolean; responseBody?: string; responseRev?: string; responsePhoto?: string; respondedOn?: string;
  accepted: boolean; acceptedBy?: string;
}

/** Compile every client change request across a review's sheets, oldest first. */
export function changeRows(r: DrawingReview): ChangeRow[] {
  const rows: ChangeRow[] = [];
  for (const s of r.sheets) {
    for (const m of s.remarks) {
      if (m.author !== "client") continue;
      rows.push({
        sheetId: s.id, sheetCode: s.code, sheetTitle: s.title, discipline: s.discipline,
        remarkId: m.id, author: m.authorName, requested: m.body, receivedOn: m.createdOn, onRevision: m.onRevision,
        requestPhoto: m.attachmentUrl,
        responded: !!m.response, responseBody: m.response?.body, responseRev: m.response?.revision,
        responsePhoto: m.response?.url, respondedOn: m.response?.respondedOn,
        accepted: !!m.response?.accepted, acceptedBy: m.response?.accepted?.by,
      });
    }
  }
  return rows.sort((a, b) => +new Date(a.receivedOn) - +new Date(b.receivedOn));
}

const cell: CSSProperties = { padding: "11px 12px", verticalAlign: "top", lineHeight: 1.45 };
const head: CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--line)", color: "var(--ink-3)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" };

export function ChangeRegister({ r, mode, onRespond, onAccept }: { r: DrawingReview; mode: "studio" | "client"; onRespond?: (sheetId: string, remarkId: string) => void; onAccept?: (sheetId: string, remarkId: string, accept: boolean) => void }) {
  const rows = changeRows(r);
  const [zoom, setZoom] = useState<string | null>(null);
  if (rows.length === 0) return null;
  const open = rows.filter((x) => !x.responded).length;
  const approved = rows.filter((x) => x.accepted).length;
  const actionCol = (mode === "studio" && onRespond) || (mode === "client" && onAccept);
  const cols = ["#", "Received", "Sheet", "Change requested", "Response — changes made", "Rev", "Area", "Status", ...(actionCol ? [""] : [])];

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginBottom: 22 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <div className="row" style={{ gap: 8 }}><Icon.report width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Change register</span></div>
        <span className="dim" style={{ fontSize: 12 }}>{rows.length} request{rows.length !== 1 ? "s" : ""} · {open} open · {approved} approved</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 760 }}>
          <thead><tr>{cols.map((h, i) => <th key={i} style={head}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={x.remarkId} style={{ borderBottom: "1px solid var(--line)", background: x.responded ? "transparent" : "color-mix(in srgb, var(--warn) 5%, transparent)" }}>
                <td style={{ ...cell, color: "var(--ink-3)" }}>{i + 1}</td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>{fmtDate(x.receivedOn)}<div className="dim" style={{ fontSize: 11 }}>{relative(x.receivedOn)}</div></td>
                <td style={cell}><span className="mono" style={{ fontWeight: 600 }}>{x.sheetCode}</span><div className="dim" style={{ fontSize: 11.5 }}>{x.sheetTitle}</div></td>
                <td style={{ ...cell, minWidth: 180, maxWidth: 280 }}>{x.requested}<div className="dim" style={{ fontSize: 11 }}>by {x.author} · rev {x.onRevision}</div></td>
                <td style={{ ...cell, minWidth: 180, maxWidth: 300 }}>{x.responseBody ? x.responseBody : <span className="dim">Not yet responded</span>}{x.respondedOn && <div className="dim" style={{ fontSize: 11 }}>{relative(x.respondedOn)}</div>}</td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>{x.responseRev ? <span className="badge accent" style={{ fontSize: 9 }}>rev {x.responseRev}</span> : <span className="dim">—</span>}</td>
                <td style={cell}>{x.responsePhoto
                  ? <button title="View the changed area" onClick={() => setZoom(x.responsePhoto!)} style={{ padding: 0, border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden", cursor: "zoom-in", background: "none", lineHeight: 0 }}><img src={x.responsePhoto} alt="changed area" style={{ width: 56, height: 40, objectFit: "cover", display: "block" }} /></button>
                  : <span className="dim">—</span>}</td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>{x.accepted
                  ? <span className="badge ok" style={{ fontSize: 9 }} title={x.acceptedBy ? `Approved by ${x.acceptedBy}` : "Approved"}><Icon.check width={9} />Approved</span>
                  : x.responded ? <span className="badge accent" style={{ fontSize: 9 }}><Icon.check width={9} />Addressed</span> : <span className="badge warn" style={{ fontSize: 9 }}>Open</span>}</td>
                {mode === "studio" && onRespond && (
                  <td style={cell}>{!x.responded
                    ? <button className="btn accent sm" style={{ whiteSpace: "nowrap" }} onClick={() => onRespond(x.sheetId, x.remarkId)}><Icon.spark width={13} />Respond</button>
                    : <button className="chip" style={{ fontSize: 11 }} onClick={() => onRespond(x.sheetId, x.remarkId)}>Edit</button>}</td>
                )}
                {mode === "client" && onAccept && (
                  <td style={cell}>{!x.responded
                    ? <span className="dim" style={{ fontSize: 11 }}>awaiting studio</span>
                    : x.accepted
                      ? <button className="chip" style={{ fontSize: 11 }} title="Withdraw approval" onClick={() => onAccept(x.sheetId, x.remarkId, false)}>Approved ✓</button>
                      : <button className="btn accent sm" style={{ whiteSpace: "nowrap" }} onClick={() => onAccept(x.sheetId, x.remarkId, true)}><Icon.check width={13} />Accept</button>}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {zoom && (
        <div onClick={() => setZoom(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.78)", display: "grid", placeItems: "center", zIndex: 100, padding: 24, cursor: "zoom-out" }}>
          <img src={zoom} alt="changed area" style={{ maxWidth: "92vw", maxHeight: "88vh", borderRadius: 10, boxShadow: "var(--shadow-lg)" }} />
        </div>
      )}
    </div>
  );
}
